import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import { ScopedAsyncStorage } from '@/lib/storage';
import { SCREEN_WIDTH, HERO_HEIGHT_MAX } from '@/constants/layout';

const COVER_IMAGE_KEY = '@fimfam/cover-image';
const DEFAULT_SENTINEL = '__DEFAULT__';

/**
 * CoverImageManager - Singleton
 * Manages home screen cover image storage locally (device only).
 * Stores image URI in AsyncStorage for persistence across app restarts.
 */
export class CoverImageManager {
  private static instance: CoverImageManager;
  private currentImageUri: string | null = null;
  private initialized = false;
  private storage: ScopedAsyncStorage | null = null;

  private constructor() {}

  static getInstance(): CoverImageManager {
    if (!CoverImageManager.instance) {
      CoverImageManager.instance = new CoverImageManager();
    }
    return CoverImageManager.instance;
  }

  /**
   * Set scoped storage and reset initialization
   */
  setStorage(storage: ScopedAsyncStorage): void {
    this.storage = storage;
    this.initialized = false;
    this.currentImageUri = null;
  }

  /**
   * Initialize by loading stored cover image URI
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const storedUri = this.storage
        ? await this.storage.getItem(COVER_IMAGE_KEY)
        : await AsyncStorage.getItem(COVER_IMAGE_KEY);
      this.currentImageUri = storedUri === DEFAULT_SENTINEL ? null : storedUri;
      this.initialized = true;
    } catch (error) {
      console.error('[CoverImageManager:initialize] Failed to load cover image:', error);
      this.currentImageUri = null;
      this.initialized = true;
    }
  }

  getCoverImageUri(): string | null {
    return this.currentImageUri;
  }

  hasCoverImage(): boolean {
    return this.currentImageUri !== null;
  }

  /**
   * Request media library permissions
   */
  private async requestPermissions(): Promise<boolean> {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    return status === 'granted';
  }

  /**
   * Pick image from device and save URI
   */
  async pickImage(): Promise<string | null> {
    try {
      const hasPermission = await this.requestPermissions();
      if (!hasPermission) {
        console.error('[CoverImageManager:pickImage] Permission denied');
        return null;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [Math.round(SCREEN_WIDTH), Math.round(HERO_HEIGHT_MAX)],
        quality: 0.8,
      });

      if (result.canceled) {
        return null;
      }

      const uri = result.assets[0].uri;
      await this.setCoverImageUri(uri);
      return uri;
    } catch (error) {
      console.error('[CoverImageManager:pickImage] Failed to pick image:', error);
      throw error;
    }
  }

  /**
   * Set cover image URI and persist to storage
   */
  async setCoverImageUri(uri: string | null): Promise<void> {
    try {
      this.currentImageUri = uri;
      const valueToStore = uri ?? DEFAULT_SENTINEL;
      if (this.storage) {
        await this.storage.setItem(COVER_IMAGE_KEY, valueToStore);
      } else {
        await AsyncStorage.setItem(COVER_IMAGE_KEY, valueToStore);
      }
    } catch (error) {
      console.error('[CoverImageManager:setCoverImageUri] Failed to save URI:', error);
      throw error;
    }
  }

  /**
   * Remove cover image
   */
  async removeCoverImage(): Promise<void> {
    await this.setCoverImageUri(null);
  }
}
